import * as yup from 'yup';

import type { EchoView } from '@betterment/js-runtime';

function isDocumentFragment(value: unknown): value is DocumentFragment {
  return value instanceof DocumentFragment;
}

const slot = yup.mixed(isDocumentFragment);

const pageProps = yup.object().shape({
  title: yup.string().required(),
  description: yup.string().nullable(),
  bodyClassName: yup.string().required(),
  csrfToken: yup.string().required(),
});

const applicationLayout = yup.object().shape({
  type: yup.string().oneOf(['view']).required(),
  status: yup.number().required(),
  props: pageProps.shape({
    layout: yup
      .string()
      .required()
      .test('is-application', 'layout must be "application"', value => value === 'application'),
  }),
  slots: yup.object().shape({
    main: slot.required(),
  }),
});

const workflowLayout = yup.object().shape({
  type: yup.string().oneOf(['view']).required(),
  status: yup.number().required(),
  props: pageProps.shape({
    layout: yup
      .string()
      .required()
      .test('is-workflow', 'layout must be "workflow"', value => value === 'workflow'),
    siteLayoutClassName: yup.string().required(),
  }),
  slots: yup.object().shape({
    header: slot.required(),
    main: slot.required(),
    footer: slot.required(),
    modals: slot.required(),
  }),
});

export type ApplicationLayout = yup.Asserts<typeof applicationLayout>;
export type WorkflowLayout = yup.Asserts<typeof workflowLayout>;

/**
 * Validates the view to ensure it complies with one of our schemas. Yup doesn't support unions, so
 * this is a bit manual. Also, we're returning a tuple here since TypeScript doesn't allow us to
 * discriminate a union based on a nested property (`view.props.layout`);
 */
export function assertValidView(view: EchoView) {
  const layout = String(view.props.layout);

  if (layout === 'application') {
    return [layout, applicationLayout.validateSync(view)] as const;
  }

  if (layout === 'workflow') {
    return [layout, workflowLayout.validateSync(view)] as const;
  }

  throw new Error(`Invalid layout: ${layout}`);
}
